import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '@constants';
import { CUSTOM_MESSAGES } from '@veriables';
import { getObjectValue, safeJsonParse } from '@utility/helperFunction';
import { runWhenIdle } from '@helpers/generic';
import store from '@store';
import { flowType } from '@utility/userInfo';
import { setSeat, setPaymentCheck, setSyncData } from '@reducers/orders';
import { syncForPayment, setIsTipOnlyEnabled } from '@reducers/storeChecks';
import { setConfirmStageSplit } from '@reducers/stage';
import { setErrorPopUp } from '@reducers/notification';
import { getTotalAmount } from '@containers/MenuOrderV2/MenuCartBarV2/CartButtons/CartActionHandler';

const getSubTotalAmount = table => +table.subTotalAmount;

const checkPendingItemsPresent = data =>
  data?.orders?.[0]?.items?.some(item => item.pending === true);

const activateGiftCards = data => {
  if (!data.cards?.length) return false;
  return (
    data.cards.some(card => card.status !== 5 && !card.void) &&
    !data.orders[0]?.items?.some(item => item?.pending === true)
  );
};

export const useGoToOrder = ({
  getSeat,
  clearCart,
  changeCartLoadingStatus,
  removePosition,
  pushCartItems,
  setPosition,
  setCurrentPage,
  setOrderNavLoading,
}) => {
  const navigate = useNavigate();

  return useCallback(
    (data, currentPage = 0) => {
      setOrderNavLoading(true);
      Promise.all([
        getSeat(data),
        clearCart(),
        changeCartLoadingStatus({ isCartLoading: true }),
      ]).then(() => {
        removePosition();
        const cartItems = getObjectValue(() => data?.orders[0]?.items, [])
          .filter(e => e.pending)
          .map(it => {
            const order = safeJsonParse(it.orderJson, {});
            const modifier = order.modifierJson
              ? safeJsonParse(order.modifierJson, {})
              : {};
            return {
              ...modifier,
              orderJson: it.orderJson,
              modifierJson: order.modifierJson,
              id: it.id,
              pending: true,
              refill: order.refill,
            };
          });
        pushCartItems(cartItems);
        const position =
          data?.seats?.filter(e => e.open).length > 0
            ? data.seats.find(e => e.open)
            : { number: -1, position: -1 };
        setPosition(position);
        window.sessionStorage.removeItem('menuTypeId');
        window.sessionStorage.removeItem('menuScrollTop');
        runWhenIdle(() => {
          setCurrentPage(currentPage);
          changeCartLoadingStatus({ isCartLoading: false });
        });
        setTimeout(() => navigate(`${APP_CONFIG.BASE_URL}/orders`));
      });
    },
    [
      getSeat,
      clearCart,
      changeCartLoadingStatus,
      removePosition,
      pushCartItems,
      setPosition,
      setCurrentPage,
      setOrderNavLoading,
      navigate,
    ]
  );
};

export const usePay = () => {
  const navigate = useNavigate();

  return useCallback(
    data => {
      store.dispatch(setIsTipOnlyEnabled(false));
      const isPendingItemsPresent = checkPendingItemsPresent(data);

      if (activateGiftCards(data) && !isPendingItemsPresent) {
        navigate(`${APP_CONFIG.BASE_URL}/giftcard/${data?.id}`, {
          replace: true,
        });
        return;
      }

      const isServerApp = flowType() === 'serverApp';
      if (isPendingItemsPresent && isServerApp) {
        store.dispatch(
          setErrorPopUp({ res: CUSTOM_MESSAGES().PAYMENT_ERROR_HAVING_UNSENT })
        );
        return;
      }

      const { oauth } = store.getState();
      const { deviceInformation, loggedInUserData } = oauth;
      const dataToSend = {
        deviceId: deviceInformation.deviceId,
        employeeId: Number(loggedInUserData?.id),
        storeId: deviceInformation.storeId,
        checkHeaderId: data.id,
        totalAmount: data.totalAmount,
        unpaidAmount: Number(data.unpaidAmount),
        paidAmount: data.paidAmount,
      };

      store
        .dispatch(syncForPayment(dataToSend))
        .then(res => {
          const syncedCheck = res.check || data;
          store.dispatch(
            setSeat({
              ...syncedCheck,
              subTotalAmount: getSubTotalAmount(syncedCheck),
              totalAmount: getTotalAmount(syncedCheck),
              control: res?.control,
              checkHeaderId: res?.checkHeaderId,
              storeId: res?.storeId,
              giftCard: res?.giftCard,
              offer: res?.offer,
              paymentCheck: res?.paymentCheck,
              paymentChecks: res?.paymentChecks,
            })
          );
          store.dispatch(
            setSyncData({
              ...syncedCheck,
              subTotalAmount: getSubTotalAmount(syncedCheck),
              totalAmount: getTotalAmount(syncedCheck),
              control: res.control,
            })
          );
          if (Number(data.unpaidAmount) === 0) return;
          store.dispatch(setConfirmStageSplit(false));
          store
            .dispatch(
              setPaymentCheck({
                ...syncedCheck,
                subTotalAmount: getSubTotalAmount(syncedCheck),
                totalAmount: getTotalAmount(syncedCheck),
              })
            )
            .then(() => {
              navigate(
                `${APP_CONFIG.BASE_URL}/receipt/${syncedCheck.id}?ticketSplitTypeId=1&splitValue=1&screenType=1`,
                { replace: true }
              );
            });
        })
        .catch(err => {
          store.dispatch(setErrorPopUp({ res: err.message }));
        });
    },
    [navigate]
  );
};

export const useIsCheckVisible = ({
  isMyCheck,
  userInfo,
  auth,
  employeeJobHashTable,
  jobList,
  teamId,
  allChecksVisible,
}) => {
  return useCallback(
    check => {
      if (isMyCheck) return userInfo?.id === check?.employeeId;

      let employeeList = {};
      jobList.forEach(j => {
        if (employeeJobHashTable[j.id]?.employees[check?.employeeId]) {
          employeeList = employeeJobHashTable[j.id].employees;
        }
      });

      const employee = auth?.employees?.find(e => e.id === check?.employeeId);
      const employeeHash = employee && employeeJobHashTable[employee.jobId];
      if (employeeHash?.checkVisibility === 3) return true;

      if (!userInfo?.jobId || !employeeJobHashTable[userInfo.jobId]) {
        return userInfo?.id === check?.employeeId;
      }

      switch (employeeJobHashTable[userInfo.jobId].checkVisibility) {
        case 0:
          return userInfo.id === check?.employeeId;
        case 1:
          return !teamId
            ? Object.keys(
                employeeJobHashTable[userInfo.jobId]?.employees || {}
              ).includes(`${check?.employeeId}`)
            : Object.keys(employeeList).includes(`${check?.employeeId}`);
        case 4:
          return !allChecksVisible ? true : userInfo.id === check?.employeeId;
        default:
          return true;
      }
    },
    [
      isMyCheck,
      userInfo,
      auth?.employees,
      employeeJobHashTable,
      jobList,
      teamId,
      allChecksVisible,
    ]
  )
};